import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { PageShell } from '@/components/layout/PageShell';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { ROUTES } from '@/config/routes';
import { cn } from '@/lib/cn';

interface AdminShellProps {
  children: ReactNode;
  /** Section marker eyebrow above the page title. */
  eyebrow: string;
}

const NAV = [
  { to: ROUTES.admin.queue, label: 'Queue' },
  { to: ROUTES.admin.safety, label: 'Safety' },
  { to: ROUTES.admin.revenue, label: 'Revenue' },
  { to: ROUTES.admin.launchGates, label: 'Launch gates' },
  { to: ROUTES.admin.secrets, label: 'Secrets' },
] as const;

/**
 * Shared chrome for every /admin/* page. Sticky tab bar across the top so
 * moderators can sweep between queue + dashboards without losing focus.
 */
export const AdminShell = ({ children, eyebrow }: AdminShellProps) => (
  <PageShell
    rail={false}
    mobileTopBar={
      <>
        {/* Identity is the WorldSwitcher in PageShell's bar — one lockup.
            This spacer keeps the page label on the right edge. */}
        <span aria-hidden />
        <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          Admin
        </span>
      </>
    }
  >
    <header className="border-b border-white/[0.06] bg-ink/95 px-5 py-5 backdrop-blur md:px-12">
      <SectionMarker className="mb-2">{eyebrow}</SectionMarker>
      <nav className="flex flex-wrap gap-x-5 gap-y-2">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'font-display text-[11px] font-bold uppercase tracking-widest',
                'transition-colors duration-fast ease-signal',
                isActive ? 'text-pink' : 'text-text-muted hover:text-text-primary',
              )
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    </header>
    {children}
  </PageShell>
);
